import { useTab, useTabList, useTabPanel, AriaTabListOptions, AriaTabPanelProps } from 'react-aria';
import { Item, useTabListState, TabListState, Node, Key, Collection } from 'react-stately';
import { useRef, ReactNode, useState, useEffect } from 'react';
import { NumericBadge, InputDropdown } from '../molecules';
import cx from 'classix';

export interface TabItem {
  id: string;
  content: ReactNode;
  /** Custom tab trigger content (e.g. icon + label). Defaults to collection rendering for `id`. */
  tabLabel?: ReactNode;
  actionCount?: number;
  onTabClick?: (key: Key | string) => void;
}

interface TabsProps extends AriaTabListOptions<TabItem> {
  children?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  fullwidth?: boolean;
  variant?: 'default' | 'side-tabs' | 'segmented';
  /** Keeps rendering tab buttons (instead of falling back to a dropdown) on mobile, and stretches them full width. Opt-in per usage. */
  keepTabsOnMobile?: boolean;
}

const AriaTabs = (props: TabsProps) => {
  const state = useTabListState(props);
  const ref = useRef(null);
  const { tabListProps } = useTabList(props, state, ref);
  const { variant = 'default', keepTabsOnMobile = false } = props;

  // we use an entirely different component for the tab list if mobile
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize(); // Set initial state
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div className={cx(variant === 'side-tabs' && 'flex')}>
      <div
        className={cx(
          !isMobile && variant === 'default' && 'border-b border-gray-100 flex flex-wrap',
          variant === 'segmented' &&
            'gap-1 rounded-xl border border-gray-100 bg-gray-50 p-1 shadow-[0_1px_2px_0_rgba(16,24,40,0.05)]',
          variant === 'segmented' && (isMobile && keepTabsOnMobile ? 'flex w-full' : 'inline-flex'),
          !isMobile && variant === 'side-tabs' && 'flex flex-col',
          variant !== 'side-tabs' && (props.orientation === 'vertical' ? 'flex-col' : 'flex-row'),
          props.orientation === 'horizontal' && variant === 'default' && 'gap-x-4',
          props.fullwidth && 'w-full justify-center',
          variant === 'side-tabs' && 'min-w-38 flex-shrink-0 pr-2',
        )}
        {...tabListProps}
        ref={ref}
      >
        <AriaTabListParent
          items={state.collection}
          state={state}
          fullwidth={props.fullwidth}
          isMobile={isMobile}
          variant={variant}
          keepTabsOnMobile={keepTabsOnMobile}
        />
      </div>
      <AriaTabPanel key={state.selectedItem?.key} state={state} variant={props.variant} />
    </div>
  );
};

const AriaTabListParent = ({
  items,
  state,
  isMobile,
  fullwidth = false,
  variant = 'default',
  keepTabsOnMobile = false,
}: {
  items: Collection<Node<TabItem>>;
  state: TabListState<TabItem>;
  isMobile: boolean;
  fullwidth?: boolean;
  variant?: TabsProps['variant'];
  keepTabsOnMobile?: boolean;
}) => {
  return !isMobile || keepTabsOnMobile ?
      <>
        {[...items].map((item) => (
          <AriaTabButton
            key={item.key}
            item={item}
            state={state}
            fullWidth={fullwidth}
            variant={variant}
            stretch={isMobile && keepTabsOnMobile}
          />
        ))}
      </>
    : <>
        <AriaTabDropDown items={items} state={state} />
      </>;
};

const AriaTabDropDown = ({
  items,
  state,
}: {
  items: Collection<Node<TabItem>>;
  state: TabListState<TabItem>;
  fullWidth?: boolean;
}) => {
  // hacky way to pass the tabState's fallback to first item to the input dropdown
  // otherwise only works if we explicitly pass defaultSelectedKey prop into Tabs
  const defaultSelectedKey = state.selectedKey ?? [...items][0].key;
  return (
    <InputDropdown
      items={[...items].map((item) => ({
        text: item.key as string,
        textValue: item.key,
      }))}
      type='default'
      onSelectionChange={(key) => state.setSelectedKey(key)}
      defaultSelectedKey={defaultSelectedKey as string}
    />
  );
};

const AriaTabButton = ({
  item,
  state,
  fullWidth,
  variant = 'default',
  stretch = false,
}: {
  item: Node<TabItem>;
  state: TabListState<TabItem>;
  fullWidth?: boolean;
  variant?: TabsProps['variant'];
  stretch?: boolean;
}) => {
  const { key, rendered, props } = item;
  const ref = useRef(null);
  const { tabProps } = useTab({ key }, state, ref);
  const isSelected = state.selectedKey === key;
  const isSegmented = variant === 'segmented';
  const isSideTabs = variant === 'side-tabs';
  return (
    <button
      {...tabProps}
      ref={ref}
      onClick={props.onTabClick}
      className={cx(
        'flex items-center gap-2 outline-none',
        isSideTabs ? 'justify-start text-left' : 'justify-center',
        fullWidth && 'min-w-60',
        stretch && 'flex-1',
        !isSegmented && !isSideTabs && 'px-1 pb-3 h-9 font-semibold',
        !isSegmented && isSideTabs && 'px-1 py-2 font-semibold border-b-2 border-transparent',
        !isSegmented && isSelected && !isSideTabs && 'border-b-2 border-brand-600 text-brand-500',
        !isSegmented && isSelected && isSideTabs && 'border-brand-600 text-brand-500',
        !isSegmented &&
          !isSelected &&
          !isSideTabs &&
          'text-gray-400 hover:border-b-2 hover:border-brand-600 hover:text-brand-500 focus:border-brand-600 focus:text-brand-500 focus:border-b-2',
        !isSegmented &&
          !isSelected &&
          isSideTabs &&
          'text-gray-400 hover:border-brand-600 hover:text-brand-500 focus:border-brand-600 focus:text-brand-500',
        isSegmented && 'rounded-lg px-4 py-2 text-sm font-semibold transition-colors',
        isSegmented &&
          isSelected &&
          'bg-white text-[#0f1728] shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] border border-gray-100',
        isSegmented && !isSelected && 'text-[#667084] hover:text-[#0f1728]',
      )}
    >
      {props.tabLabel ?? rendered}
      {props.actionCount && <NumericBadge size='sm' color='error' number={props.actionCount} />}
    </button>
  );
};

interface TabPanelProps extends AriaTabPanelProps {
  state: TabListState<TabItem>;
  variant?: TabsProps['variant'];
}

const AriaTabPanel = ({ state, ...props }: TabPanelProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { tabPanelProps } = useTabPanel(props, state, ref);
  return (
    <div {...tabPanelProps} ref={ref} className={props.variant === 'side-tabs' ? 'flex-grow' : ''}>
      {state.selectedItem?.props.children}
    </div>
  );
};

interface MWTabsProps {
  title: string;
  items: TabItem[];
  defaultSelectedKey?: string;
  fullWidth?: boolean;
  orientation?: 'horizontal' | 'vertical';
  onSelectionChange?: (key: Key) => void;
  variant?: 'default' | 'side-tabs' | 'segmented';
  /** Keeps rendering tab buttons (instead of falling back to a dropdown) on mobile, and stretches them full width. Opt-in per usage. */
  keepTabsOnMobile?: boolean;
}

export const Tabs = (props: MWTabsProps) => {
  const {
    title,
    items,
    defaultSelectedKey,
    onSelectionChange,
    fullWidth = false,
    orientation = 'horizontal',
    variant = 'default',
    keepTabsOnMobile = false,
  } = props;

  return (
    <AriaTabs
      items={items}
      aria-label={title}
      defaultSelectedKey={defaultSelectedKey}
      onSelectionChange={onSelectionChange}
      orientation={orientation}
      fullwidth={fullWidth}
      keyboardActivation='manual'
      variant={variant}
      keepTabsOnMobile={keepTabsOnMobile}
    >
      {items.map((item) => (
        <Item key={item.id} title={item.id} {...item}>
          {item.content}
        </Item>
      ))}
    </AriaTabs>
  );
};
